import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FileText, AlertCircle } from 'lucide-react';
import SearchableFilterSelect from 'components/common/SearchableFilterSelect';
import { FinanceiroTabela } from 'features/financeiro/components/financeiro';
import api from 'services/api';
import { LoadingSpinner } from 'components/common';
import { getCompanyContext } from 'utils/setCompanyContext';

const tipoMap = {
  'R': 'RECEITAS',
  'D': 'DESPESAS',
  'P': 'PASSIVO',
  'A': 'ATIVO'
};

const DRESection = () => {
  const [loading, setLoading] = useState(false);
  const [dadosMeses, setDadosMeses] = useState([]); // Dados dos 12 meses
  const [erro, setErro] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showAllMonths, setShowAllMonths] = useState(false);
  const monthSelectId = 'dre-mobile-month-select';
  
  // Obter empresa do contexto
  const getEmpresaFromContext = useCallback(() => {
    const context = getCompanyContext();
    return context?.companies?.[0]?.name || 'MOV Fibra';
  }, []);

  const colunas = [
    { chave: 'descricao', titulo: 'Descrição', alinhamento: 'left' },
    { chave: 'valor', titulo: 'Valor', alinhamento: 'right', formato: 'moeda' }
  ];

  // Carregar dados do DRE
  const carregarDre = useCallback(async () => {
    setLoading(true);
    setErro(null);
    
    try {
      const empresa = getEmpresaFromContext();
      
      // Sempre usar o mês atual
      const hoje = new Date();
      const ano = hoje.getFullYear();
      const mes = String(hoje.getMonth() + 1).padStart(2, '0');
      const mesAtual = `${ano}-${mes}`; // Formato: "2025-11"

      // Carregar DRE dos últimos 12 meses
      const responseDetalhado = await api.post('/n8n/adm/dre-detalhado-12-meses', {
        empresa: empresa,
        mes_atual: mesAtual
      });

      if (responseDetalhado.data.success) {
        // Estrutura esperada: { meses: [{ mes: "2024-12", mes_formatado: "12/2024", dados: [...] }, ...] }
        const meses = responseDetalhado.data.data?.meses || [];
        setDadosMeses(meses);
      } else {
        setErro('Erro ao carregar dados do DRE');
      }
    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
      setErro(error.response?.data?.message || 'Erro ao carregar DRE');
    } finally {
      setLoading(false);
    }
  }, [getEmpresaFromContext]);

  // Carregar DRE ao montar o componente
  useEffect(() => {
    carregarDre();
  }, [carregarDre]);

  // Recarregar dados quando a empresa mudar
  useEffect(() => {
    const handleCompanyChange = () => {
      carregarDre();
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [carregarDre]);

  // Processar dados para o formato da tabela
  const processarDadosParaTabela = useCallback((dados) => {
    if (!dados || !Array.isArray(dados) || dados.length === 0) {
      return [];
    }

    const dadosTabela = [];
      const grupos = {};
    let totalReceitas = 0;
    let totalDespesas = 0;
      
      dados.forEach(item => {
        if (item.tipo && item.total) {
          if (!grupos[item.tipo]) {
            grupos[item.tipo] = [];
          }
        const valor = parseFloat(item.total) || 0;
          grupos[item.tipo].push({
            descricao: item.planejamento || item.descricao || `Item ${item.tipo}`,
          valor: valor
        });
        
        // Calcular totais considerando os sinais
        if (item.tipo === 'R') {
          // Receitas: somar o valor como está (pode ser positivo ou negativo)
          totalReceitas += valor;
        } else if (item.tipo === 'D') {
          // Despesas: somar todas as despesas
          totalDespesas += valor;
        }
        }
      });

      // Adicionar cabeçalhos de grupo e itens
      Object.keys(grupos).forEach(tipo => {
        // Adicionar cabeçalho do grupo com destaque
        dadosTabela.push({
          descricao: tipoMap[tipo] || `Tipo ${tipo}`,
          valor: null, // Cabeçalho sem valor
          destaque: true, // Marcar como cabeçalho
          cor: 'text-[color:var(--text-primary)]', // Cor do texto
          statusBg: 'bg-[color:var(--surface-muted)]', // Fundo destacado
          statusCor: 'text-[color:var(--text-primary)]' // Cor do texto
        });
        
        // Adicionar itens do grupo
        grupos[tipo].forEach(item => {
          dadosTabela.push(item);
        });
      });

    // Adicionar linha separadora antes dos totais
    dadosTabela.push({
      descricao: '',
      valor: null,
      destaque: false,
      isSeparator: true
    });

    // Adicionar total de receitas (sempre mostrar como positivo na exibição)
    // Mas usar o valor real (com sinal) para o cálculo
    dadosTabela.push({
      descricao: 'Total de Receitas',
      valor: totalReceitas, // Manter o sinal para cálculo correto
      destaque: true,
      cor: 'text-[color:var(--text-primary)]',
      statusBg: 'bg-[color:var(--surface-strong)]',
      statusCor: 'text-[color:var(--chart-card-blue-text)]',
      isTotal: true
    });

    // Adicionar total de despesas (soma de todas as despesas)
    dadosTabela.push({
      descricao: 'Total de Despesas',
      valor: totalDespesas, // Soma de todas as despesas
      destaque: true,
      cor: 'text-[color:var(--text-primary)]',
      statusBg: 'bg-[color:var(--surface-strong)]',
      statusCor: 'text-[color:var(--chart-card-orange-text)]',
      isTotal: true
    });

    // Calcular e adicionar lucro líquido/prejuízo
    // Lucro Líquido = Receitas + Despesas (despesas já vêm negativas)
    const lucroLiquido = totalReceitas + totalDespesas;
    dadosTabela.push({
      descricao: lucroLiquido >= 0 ? 'Lucro Líquido' : 'Prejuízo',
      valor: lucroLiquido, // Mostrar o valor real com sinal
      destaque: true,
      cor: lucroLiquido >= 0 ? 'text-[color:var(--chart-positive)]' : 'text-[color:var(--chart-negative)]',
      statusBg: 'bg-[color:var(--surface-strong)]',
      statusCor: lucroLiquido >= 0 ? 'text-[color:var(--chart-positive)]' : 'text-[color:var(--chart-negative)]',
      isTotal: true,
      isLucro: true
    });

    return dadosTabela;
  }, []);

  // Processar dados dos 12 meses
  const dadosMesesProcessados = useMemo(() => {
    if (!dadosMeses || dadosMeses.length === 0) {
      return [];
    }

    return dadosMeses.map((mesData) => ({
      mes: mesData.mes,
      mesFormatado: mesData.mes_formatado,
      dados: processarDadosParaTabela(mesData.dados || [])
    }));
  }, [dadosMeses, processarDadosParaTabela]);

  const mesesOrdenados = useMemo(() => {
    return [...dadosMesesProcessados].sort((a, b) => (a.mes > b.mes ? -1 : 1));
  }, [dadosMesesProcessados]);

  useEffect(() => {
    if (mesesOrdenados.length === 0) {
      if (selectedMonth) {
        setSelectedMonth('');
      }
      return;
    }

    const selectedMonthStillExists = mesesOrdenados.some(
      (mesData) => mesData.mesFormatado === selectedMonth
    );

    if (!selectedMonthStillExists) {
      setSelectedMonth(mesesOrdenados[0].mesFormatado);
    }
  }, [mesesOrdenados, selectedMonth]);

  // Processar dados para tabela com múltiplas colunas mantendo estrutura hierárquica
  const dadosTabelaMeses = useMemo(() => {
    if (dadosMesesProcessados.length === 0) return [];

    // Usar a estrutura do primeiro mês como base (todos devem ter a mesma estrutura)
    const estruturaBase = dadosMesesProcessados[0]?.dados || [];
    const linhas = [];

    estruturaBase.forEach((itemBase) => {
      const linha = {
        descricao: itemBase.descricao,
        isSeparator: itemBase.isSeparator,
        isTotal: itemBase.isTotal,
        isLucro: itemBase.isLucro,
        destaque: itemBase.destaque,
        cor: itemBase.cor,
        statusBg: itemBase.statusBg,
        statusCor: itemBase.statusCor
      };

      // Adicionar valores de cada mês
      dadosMesesProcessados.forEach((mesData) => {
        let itemMes = null;

        // Para itens de lucro/prejuízo, buscar pelo isLucro em vez da descrição
        // pois a descrição pode variar entre "Lucro" e "Prejuízo"
        if (itemBase.isLucro) {
          itemMes = mesData.dados.find((d) => d.isLucro === true);
        } else {
          // Para outros itens, buscar pela descrição e flags
          itemMes = mesData.dados.find(
            (d) =>
              d.descricao === itemBase.descricao &&
              d.isTotal === itemBase.isTotal &&
              d.isSeparator === itemBase.isSeparator
          );
        }

        linha[`mes_${mesData.mes}`] = itemMes?.valor ?? null;
      });

      linhas.push(linha);
    });

    return linhas;
  }, [dadosMesesProcessados]);

  const mesesVisiveisMobile = useMemo(
    () => (showAllMonths ? mesesOrdenados : mesesOrdenados.filter((mes) => mes.mesFormatado === selectedMonth)),
    [showAllMonths, mesesOrdenados, selectedMonth]
  );

  // Verificar se os dados estão disponíveis
  if (loading) {
    return (
      <LoadingSpinner
        label="Carregando DRE"
        subtitle="Aguarde enquanto carregamos os dados..."
        className="min-h-[360px] w-full"
        cardClassName="max-w-none w-full"
      />
    );
  }

  if (erro) {
    return (
      <div className="overflow-hidden">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center">
            <FileText className="h-5 w-5 text-[color:var(--accent)] mr-2 flex-shrink-0" />
            <span className="break-words leading-tight">DRE - Demonstrativo do Resultado do Exercício</span>
          </h3>
          </div>
         
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-600">Erro ao carregar DRE</h3>
              <p className="text-sm text-red-500 mt-1">{erro}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dadosMesesProcessados || dadosMesesProcessados.length === 0) {
    return (
      <div className="overflow-hidden">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center">
            <FileText className="h-5 w-5 text-[color:var(--accent)] mr-2 flex-shrink-0" />
            <span className="break-words leading-tight">DRE - Demonstrativo do Resultado do Exercício</span>
          </h3>
          </div>
        </div>
        
        <div className="text-center text-[color:var(--text-muted)] py-8">
          Nenhum dado disponível para o DRE
        </div>
      </div>
    );
  }


  return (
    <div className="overflow-hidden">
      
      
      <>
          {/* Desktop: Tabela com primeira coluna fixa e scroll horizontal */}
          <div className="hidden md:block">
        <div className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--header-control-border-subtle)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[color:var(--header-control-border-subtle)]">
              <thead className="bg-[color:var(--surface)] sticky top-0 z-30">
                <tr>
                  {/* Primeira coluna fixa (Descrição) */}
                  <th scope="col" className="sticky left-0 z-20 min-w-[280px] w-[320px] bg-[color:var(--surface)] border-r border-[color:var(--header-control-border-subtle)] px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider shadow-sm">
                    Descrição
                  </th>
                  {/* Colunas de meses com scroll */}
                  {mesesOrdenados.map((mes, index) => (
                    <th
                      key={mes.mes}
                      scope="col"
                      className={`px-4 py-3 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap ${
                        index === mesesOrdenados.length - 1 ? 'pr-4' : ''
                      }`}
                    >
                      {mes.mesFormatado}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--header-control-border-subtle)]">
                {dadosTabelaMeses.map((linha, linhaIndex) => {
                  // Verificar se é separador
                  if (linha.isSeparator) {
              return (
                      <tr key={linhaIndex} className="border-t border-[color:var(--header-control-border-subtle)]">
                        <td className="sticky left-0 z-10 bg-[color:var(--surface)] border-r border-[color:var(--header-control-border-subtle)] px-4 py-2"></td>
                        {mesesOrdenados.map((mes) => (
                          <td key={mes.mes} className="px-4 py-2"></td>
                        ))}
                      </tr>
                    );
                  }

                  // Determinar background da linha para aplicar na coluna fixa
                  const bgClassLinha = linha.destaque 
                    ? (linha.statusBg || 'bg-[color:var(--surface-muted)]')
                    : 'bg-[color:var(--surface)]';
                  const bgClassHover = linha.destaque
                    ? (linha.isTotal ? 'bg-[color:var(--surface-strong)]' : (linha.statusBg || 'bg-[color:var(--surface-muted)]'))
                    : 'hover:bg-[color:var(--surface-muted)]/45';

                    return (
                      <tr 
                        key={linhaIndex} 
                        className={`transition-colors ${bgClassHover}`}
                      >
                        {/* Primeira coluna fixa (Descrição) */}
                        <td
                          className={`sticky left-0 z-10 min-w-[280px] w-[320px] whitespace-nowrap border-r border-[color:var(--header-control-border-subtle)] px-4 py-3 text-sm ${bgClassLinha} ${
                            linha.cor || linha.statusCor || 'text-[color:var(--text-primary)]'
                          } ${linha.isTotal ? 'font-semibold' : 'font-medium'} shadow-sm`}
                        >
                          {linha.descricao || ''}
                        </td>
                      {/* Colunas de valores com scroll */}
                      {mesesOrdenados.map((mes) => {
                        const valor = linha[`mes_${mes.mes}`];
            return (
                          <td
                            key={mes.mes}
                          className={`px-4 py-3 text-sm text-right whitespace-nowrap ${
                              linha.isTotal 
                                ? `font-bold ${
                                    linha.isLucro 
                                      ? (valor >= 0 ? 'text-[color:var(--chart-positive)]' : 'text-[color:var(--chart-negative)]')
                                      : linha.statusCor || 'text-[color:var(--text-primary)]'
                                  }`
                                : 'text-[color:var(--text-primary)]'
                            } ${linha.isTotal ? 'bg-[color:var(--surface-strong)]' : ''}`}
                          >
                            {valor !== null && valor !== undefined
                              ? parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                              : '-'
                            }
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
                  </div>
                </div>
              </div>
      
        {/* Mobile: Cards empilhados - um card por mês */}
        <div className="md:hidden">
        <div className="sticky top-0 z-20 mb-4 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/92 p-3 backdrop-blur-sm">
          <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <label htmlFor={monthSelectId} className="text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.12em]">
              Mês
            </label>
            <div className="w-full">
              <SearchableFilterSelect
                inputId={monthSelectId}
                ariaLabel="Selecionar mês para visualizar o DRE"
                options={mesesOrdenados.map((mesData) => mesData.mesFormatado)}
                value={selectedMonth}
                onChange={setSelectedMonth}
                placeholder="Selecione o mês"
                className="w-full"
                dropdownClassName="max-h-64"
                compact
              />
            </div>
          </div>
          <button
            onClick={() => setShowAllMonths(!showAllMonths)}
            aria-pressed={showAllMonths}
            className="inline-flex min-h-[44px] items-center self-start rounded-lg border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70"
          >
            {showAllMonths ? 'Mostrar somente o mês selecionado' : 'Mostrar todos os meses'}
          </button>
        </div>
        </div>

        <div className="space-y-4">
          {mesesVisiveisMobile.map((mesData) => (
            <div key={mesData.mes} className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--header-control-border-subtle)] shadow-sm overflow-hidden">
              {/* Cabeçalho do card com o mês */}
              <div className="bg-[color:var(--surface-muted)] border-b border-[color:var(--header-control-border-subtle)] px-4 py-3">
                <h4 className="text-base font-semibold text-[color:var(--text-primary)]">
                  {mesData.mesFormatado}
                </h4>
                </div>
              
              {/* Tabela do mês */}
              <div className="overflow-hidden">
        <FinanceiroTabela
          colunas={colunas}
                  dados={mesData.dados}
          compacto={true}
                  altura="auto"
        />
      </div>
                </div>
          ))}
         </div>
       </div>
      </>
     </div>
   );
 };
 
 export default DRESection;
